import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';

import type {
  Action,
  Dispatch,
  InfluencerCreatorNetworkListState,
  NetworkGraphNodeRaw,
  ThunkAction
} from 'types';

const { INFLUENCERS } = API.END_POINTS;

const CREATOR_NETWORK_LIST_LOADING = 'influencer/CREATOR_NETWORK_LIST_LOADING';
const CREATOR_NETWORK_LIST_LOADED = 'influencer/CREATOR_NETWORK_LIST_LOADED';
const CREATOR_NETWORK_LIST_CONNECTIONS_LOADED = 'influencer/CREATOR_NETWORK_LIST_CONNECTIONS_LOADED';
const CREATOR_NETWORK_LIST_RESET = 'influencer/CREATOR_NETWORK_LIST_RESET';
const CREATOR_NETWORK_LIST_CONNECTIONS_RESET = 'influencer/CREATOR_NETWORK_LIST_CONNECTIONS_RESET';

const initialState = {
  list: [],
  connections: [],
  isLoading: {
    list: false,
    connections: false
  }
};

const setLoadingStatus = (status: boolean, isConnections: boolean): Action => ({
  type: CREATOR_NETWORK_LIST_LOADING,

  payload: {
    status,
    isConnections
  }
});

const listLoaded = (list: NetworkGraphNodeRaw): Action => ({
  type: CREATOR_NETWORK_LIST_LOADED,

  payload: {
    list
  }
});

const connectionsLoaded = (connections: NetworkGraphNodeRaw): Action => ({
  type: CREATOR_NETWORK_LIST_CONNECTIONS_LOADED,

  payload: {
    connections
  }
});

export const resetList = (): Action => ({
  type: CREATOR_NETWORK_LIST_RESET
});

export const resetConnections = (): Action => ({
  type: CREATOR_NETWORK_LIST_CONNECTIONS_RESET
});

export const fetchList = (influencerProfileId: number, isConnections: boolean): ThunkAction => async (dispatch: Dispatch) => { // eslint-disable-line consistent-return
  dispatch(setLoadingStatus(true, isConnections));

  try {
    const url = INFLUENCERS.getNotableFollowersList(influencerProfileId, false);
    const response = await get(url, {}, { suppressGlobalNotification: true });
    const results = response.data || initialState;

    dispatch(setLoadingStatus(false, isConnections));
    return dispatch(isConnections ? connectionsLoaded(results) : listLoaded(results));
  } catch (error) {
    dispatch(setLoadingStatus(false, isConnections));
    logError(error);
  }
};

const handleListLoading = (state: InfluencerCreatorNetworkListState, { status, isConnections }): InfluencerCreatorNetworkListState => ({
  ...state,

  isLoading: {
    list: !isConnections ? status : state.isLoading.list,
    connections: isConnections ? status : state.isLoading.connections
  }
});

const handleListLoaded = (state: InfluencerCreatorNetworkListState, { list }): InfluencerCreatorNetworkListState => ({
  ...state,
  list,

  isLoading: {
    ...state.isLoading,
    list: false
  }
});

const handleConnectionsLoaded = (state: InfluencerCreatorNetworkListState, { connections }): InfluencerCreatorNetworkListState => ({
  ...state,
  connections,

  isLoading: {
    ...state.isLoading,
    connections: false
  }
});

const handleListReset = (state: InfluencerCreatorNetworkListState): InfluencerCreatorNetworkListState => ({
  ...state,
  list: initialState.list,

  isLoading: {
    ...state.isLoading,
    list: false
  }
});

const handleConnectionsReset = (state: InfluencerCreatorNetworkListState): InfluencerCreatorNetworkListState => ({
  ...state,
  connections: initialState.connections,

  isLoading: {
    ...state.isLoading,
    connections: false
  }
});

export default createReducer<any, any>('influencerCreatorNetworkList', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [CREATOR_NETWORK_LIST_LOADING]: handleListLoading,
  [CREATOR_NETWORK_LIST_LOADED]: handleListLoaded,
  [CREATOR_NETWORK_LIST_CONNECTIONS_LOADED]: handleConnectionsLoaded,
  [CREATOR_NETWORK_LIST_RESET]: handleListReset,
  [CREATOR_NETWORK_LIST_CONNECTIONS_RESET]: handleConnectionsReset
});
